import { useEffect, useState } from "react";
import { ApiError, type PinStatusResponse } from "@keydials/api-client";
import { api, getAccessToken } from "../../auth/AuthContext";
import { Modal } from "./Modal";

/**
 * Self-service unlock PIN (KEY-268). Reachable only from a full session — the server refuses these routes to
 * a PIN-unlocked one, so a PIN can never be changed from behind the lock screen it opens.
 *
 * A PIN is per-account rather than per-dial deliberately: the audit trail on every status change is a user
 * id, and a PIN shared across a ward would erase exactly the accountability the shared device is meant to
 * preserve.
 */
export function PinSetupModal({ onClose }: { onClose: () => void }) {
  const [status, setStatus] = useState<PinStatusResponse | null>(null);
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const token = getAccessToken();
    if (!token) return;
    api.pinStatus(token).then(setStatus).catch(() => setStatus(null));
  }, []);

  async function save() {
    if (pin !== confirmPin) {
      setError("The two PINs don't match.");
      return;
    }
    const token = getAccessToken();
    if (!token) return;

    setBusy(true);
    setError(null);
    try {
      await api.setPin(token, pin);
      setSaved(true);
      setPin("");
      setConfirmPin("");
      setStatus(await api.pinStatus(token));
    } catch (e) {
      // The server's own wording — it explains exactly which rule the PIN broke.
      setError(e instanceof ApiError ? e.message : "Could not save that PIN.");
    } finally {
      setBusy(false);
    }
  }

  async function clear() {
    const token = getAccessToken();
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      await api.clearPin(token);
      setSaved(false);
      setStatus(await api.pinStatus(token));
    } catch {
      setError("Could not remove your PIN.");
    } finally {
      setBusy(false);
    }
  }

  const digitsOnly = (value: string) => value.replace(/\D/g, "").slice(0, 6);
  const canSave = pin.length >= 4 && confirmPin.length >= 4 && !busy;

  return (
    <Modal
      title="Shared-device PIN"
      subtitle="Unlocks a wall tablet that has gone to its lock screen"
      onClose={onClose}
      width={460}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14, fontSize: 14 }}>
        {status && !status.available ? (
          <p style={{ margin: 0, color: "var(--text-muted)", lineHeight: 1.5 }}>
            None of your dials uses a shared device, so a PIN wouldn't unlock anything yet. Your dial
            administrator can turn shared-device locking on from the dial's settings.
          </p>
        ) : (
          <>
            <p style={{ margin: 0, color: "var(--text-muted)", lineHeight: 1.5 }}>
              Choose 4 to 6 digits. Avoid a repeated digit or a run, like 1111 or 1234. Your PIN only unlocks
              the dial already open on the device — it is never a way in to anything else.
            </p>

            <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <span style={{ fontWeight: 600 }}>{status?.hasPin ? "New PIN" : "PIN"}</span>
              <input
                type="password"
                inputMode="numeric"
                autoComplete="off"
                value={pin}
                onChange={(e) => { setPin(digitsOnly(e.target.value)); setSaved(false); setError(null); }}
                style={inputStyle}
              />
            </label>

            <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <span style={{ fontWeight: 600 }}>Confirm PIN</span>
              <input
                type="password"
                inputMode="numeric"
                autoComplete="off"
                value={confirmPin}
                onChange={(e) => { setConfirmPin(digitsOnly(e.target.value)); setSaved(false); setError(null); }}
                style={inputStyle}
              />
            </label>

            {error && <div role="alert" style={{ color: "var(--st-overdue)" }}>{error}</div>}
            {saved && <div style={{ color: "var(--st-ok, var(--text-muted))" }}>PIN saved.</div>}

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
              {status?.hasPin && (
                <button type="button" className="kd-btn" disabled={busy} onClick={() => void clear()}>
                  Remove PIN
                </button>
              )}
              <button type="button" className="kd-btn-primary" disabled={!canSave} onClick={() => void save()}>
                {status?.hasPin ? "Change PIN" : "Set PIN"}
              </button>
            </div>

            {/* No "forgot my PIN" flow by design for now: signing in with an emailed code and setting a new
                one is the reset, and it needs no extra recovery channel to attack. */}
            <p style={{ margin: 0, fontSize: 12.5, color: "var(--text-faint)", lineHeight: 1.5 }}>
              Forgotten it? Sign in with your email address as usual, then set a new one here.
            </p>
          </>
        )}
      </div>
    </Modal>
  );
}

const inputStyle: React.CSSProperties = {
  font: "inherit",
  fontSize: 18,
  letterSpacing: "0.35em",
  padding: "10px 12px",
  borderRadius: "var(--radius-sm)",
  border: "var(--border-w) solid var(--border)",
  background: "var(--surface)",
  color: "var(--text)",
};
